import { getSupportCategoryLabelForCurrentLanguage } from "@/utils/supportCategoryLabel";
import {
  BaseSupportCase,
  SupportCaseDetails,
  SupportComment,
  SupportMessage,
  HistoryEvent,
} from "@/types/support";
import { FileObject } from "@/types/shared/file";

type ApiUser = {
  id?: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
};

type ApiCase = {
  id: string;
  carIdentityNumber?: string;
  registrationNumber?: string;
  type: "complaint" | "service";
  title?: string;
  description: string;
  category: string;
  priority?: string;
  status: string;
  tags?: string[];
  damageLocation?: string;
  damageType?: string;
  engineComponent?: string;
  interiorSection?: string;
  wheelType?: string;
  customer?: ApiUser;
  assignedTo?: ApiUser;
  createdAt: string;
  updatedAt: string;
  comments?: ApiComment[];
  messages?: ApiMessage[];
  documents?: ApiDocument[];
  historyEvents?: ApiHistoryEvent[];
  hasUnread?: boolean;
};

type ApiHistoryEvent = {
  id: string;
  type: string;
  action: string;
  timestamp: string;
  userId?: string;
  userName?: string;
  details?: { oldValue?: string; newValue?: string };
};

type ApiComment = {
  id: string;
  content: string;
  isInternal?: boolean;
  author?: ApiUser;
  createdAt: string;
};

type ApiMessage = {
  id: string;
  content: string;
  senderType: "customer" | "admin";
  sender?: ApiUser;
  createdAt: string;
};

type ApiDocument = {
  id: string;
  name?: string;
  originalName?: string;
  url?: string;
  size?: number;
  mimeType?: string;
};

const SERVICE_CATEGORY_TO_API: Record<string, string> = {
  technical_issue: "technical",
  buying_selling: "auction",
  other: "other",
};

const API_CATEGORY_TO_FRONTEND: Record<string, string> = {
  technical: "technical_issue",
  billing: "other",
  auction: "buying_selling",
  vehicle: "engine",
  account: "technical_issue",
  other: "other",
};

const STATUS_TO_API: Record<string, string> = {
  not_started: "open",
  in_progress: "ongoing",
  resolved: "closed",
  closed: "closed",
};

const STATUS_FROM_API: Record<string, BaseSupportCase["status"]> = {
  open: "not_started",
  ongoing: "in_progress",
  closed: "closed",
};

export function mapFrontendStatusToApi(
  status?: string,
): string | undefined {
  if (!status || status === "all") return undefined;
  return STATUS_TO_API[status] ?? status;
}

export function mapFrontendCategoryToApi(
  type: "service" | "complaint",
  category: string,
): { category: string; tags?: string[] } {
  if (type === "complaint") {
    return { category: "vehicle", tags: [category] };
  }
  return {
    category: SERVICE_CATEGORY_TO_API[category] ?? "other",
    tags: [category],
  };
}

function resolveDisplayCategory(apiCase: ApiCase): string {
  const tag = apiCase.tags?.[0];
  if (tag) return tag;
  if (apiCase.type === "complaint") {
    return apiCase.category;
  }
  return API_CATEGORY_TO_FRONTEND[apiCase.category] ?? apiCase.category;
}

function formatAuthorName(user?: ApiUser): string {
  if (!user) return "Unknown";
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return fullName || user.username || user.email || "Unknown";
}

function mapApiDocument(doc: ApiDocument): FileObject {
  return {
    id: doc.id,
    url: doc.url || "",
    name: doc.name || doc.originalName || "document",
    type: doc.mimeType || "application/octet-stream",
    size: doc.size || 0,
  };
}

function mapApiComment(comment: ApiComment, caseId: string): SupportComment {
  return {
    id: comment.id,
    supportCaseId: caseId,
    authorId: comment.author?.id || "",
    authorName: formatAuthorName(comment.author),
    content: comment.content,
    createdAt: comment.createdAt,
    isInternal: !!comment.isInternal,
  };
}

function mapApiMessage(message: ApiMessage, caseId: string): SupportMessage {
  return {
    id: message.id,
    supportCaseId: caseId,
    content: message.content,
    senderType: message.senderType,
    senderName: formatAuthorName(message.sender),
    createdAt: message.createdAt,
  };
}

function parseVehicleRegFromDescription(description: string): string | undefined {
  const match = description.match(/^Registreringsnummer:\s*(.+?)(?:\n|$)/i);
  return match?.[1]?.trim();
}

function mapApiHistoryEvent(event: ApiHistoryEvent, caseId: string) {
  return {
    id: event.id,
    supportCaseId: caseId,
    type: event.type as HistoryEvent["type"],
    action: event.action,
    timestamp: event.timestamp,
    userId: event.userId || "",
    userName: event.userName || "System",
    details: event.details,
  };
}

export function mapApiCaseToDetails(apiCase: ApiCase): SupportCaseDetails {
  const customerName = formatAuthorName(apiCase.customer);
  const caseRef =
    apiCase.carIdentityNumber || apiCase.registrationNumber || apiCase.id;
  const vehicleRegNr =
    apiCase.type === "complaint"
      ? parseVehicleRegFromDescription(apiCase.description)
      : undefined;

  return {
    id: apiCase.id,
    customerId: apiCase.customer?.id || "",
    customerName,
    customerEmail: apiCase.customer?.email || "",
    type: apiCase.type,
    category: resolveDisplayCategory(apiCase),
    status: STATUS_FROM_API[apiCase.status] ?? "not_started",
    priority: (apiCase.priority as BaseSupportCase["priority"]) || "medium",
    created: apiCase.createdAt,
    lastUpdated: apiCase.updatedAt,
    caseRef,
    vehicleRegNr,
    regNr: vehicleRegNr,
    description: apiCase.description,
    files: (apiCase.documents || []).map(mapApiDocument),
    comments: (apiCase.comments || []).map((c) => mapApiComment(c, apiCase.id)),
    messages: (apiCase.messages || []).map((m) => mapApiMessage(m, apiCase.id)),
    historyEvents: (apiCase.historyEvents || []).map((e) =>
      mapApiHistoryEvent(e, apiCase.id),
    ),
    hasUnread: !!apiCase.hasUnread,
    damageLocation: apiCase.damageLocation,
    damageType: apiCase.damageType,
    engineComponent: apiCase.engineComponent,
    interiorSection: apiCase.interiorSection,
    wheelType: apiCase.wheelType,
  };
}

export function mapApiCaseToBase(apiCase: ApiCase): BaseSupportCase {
  const details = mapApiCaseToDetails(apiCase);
  return {
    id: details.id,
    customerName: details.customerName,
    customerEmail: details.customerEmail,
    type: details.type,
    category: details.category,
    status: details.status,
    priority: details.priority,
    created: details.created,
    lastUpdated: details.lastUpdated,
    regNr: details.regNr,
    caseRef: details.caseRef,
    hasUnread: details.hasUnread,
  };
}

export function buildCaseTitle(
  type: "service" | "complaint",
  category: string,
): string {
  const label = getSupportCategoryLabelForCurrentLanguage(category);
  return type === "complaint" ? `Reklamation: ${label}` : `Support: ${label}`;
}

export function buildCaseDescription(
  description: string,
  regNr?: string,
): string {
  if (!regNr?.trim()) return description;
  return `Registreringsnummer: ${regNr.trim()}\n\n${description}`;
}

export type { ApiCase, ApiComment, ApiMessage, ApiDocument };
